import type { DirectiveOptions } from 'vue'
import { Vue } from 'vue-property-decorator'

interface FocusTrapData {
  focusableElements: HTMLElement[]
  boundHandleKeydown: (event: KeyboardEvent) => void
  previousActiveElement: HTMLElement | null
  triggerElement: HTMLElement | null
}

type FocusableElement =
  | HTMLAnchorElement
  | HTMLButtonElement
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement

function getFocusableElementsIn(el: HTMLElement): HTMLElement[] {
  // Get all focusable elements within the container
  return Array.from(
    el.querySelectorAll<FocusableElement>('button, [href], input, select, textarea, div[tabindex]'),
  ).filter((element) => {
    const isFocusable = element.getAttribute('tabindex') !== '-1'
    return !(element as Exclude<FocusableElement, HTMLAnchorElement>).disabled && isFocusable
  })
}

function handleKeydown(event: KeyboardEvent, el: HTMLElement, data: FocusTrapData) {
  // Only handle Tab key for focus trapping. Screen readers also use up and down arrow keys
  // to navigate but it is hanbled by `[role="dialog"]` attribute.
  if (event.key !== 'Tab') return

  // If there are no focusable elements, don't trap focus
  if (data.focusableElements.length === 0) {
    return
  }

  const currentElement = document.activeElement as HTMLElement

  // Check if the current element is within our container
  if (!el.contains(currentElement)) {
    // If focus is outside our container, focus the first element
    data.focusableElements[0].focus()
    return
  }

  const currentIndex = data.focusableElements.indexOf(currentElement)

  // If current element is not in our focusable list, focus the first element
  if (currentIndex === -1) {
    data.focusableElements[0].focus()
    return
  }

  if (event.shiftKey) {
    // Shift + Tab: move backwards
    if (currentIndex === 0) {
      event.preventDefault()
      data.focusableElements[data.focusableElements.length - 1].focus()
    }
  } else {
    // Tab: move forwards
    if (currentIndex === data.focusableElements.length - 1) {
      event.preventDefault()
      data.focusableElements[0].focus()
    }
  }
}

/**
 * Vue directive that implements focus trapping functionality for
 * pseduo-modal elements like RemoveCartItemOverlay.
 *
 * It's used mainly for accessibility reasons.
 *
 * @example
 * ```vue
 * <!-- Basic usage -->
 * <div v-focus-trap class="modal">
 *   <button>First</button>
 *   <input type="text" />
 *   <button>Last</button>
 * </div>
 *
 * <!-- With trigger element -->
 * <div v-focus-trap="{ triggerElement: openButton }" class="modal">
 *   <!-- modal content -->
 * </div>
 * ```
 */
const focusTrapDataMap = new WeakMap<HTMLElement, FocusTrapData>()

const focusTrap: DirectiveOptions = {
  inserted(el: HTMLElement, binding) {
    const data: FocusTrapData = {
      focusableElements: [],
      boundHandleKeydown: (event: KeyboardEvent) => handleKeydown(event, el, data),
      previousActiveElement: document.activeElement as HTMLElement,
      triggerElement: binding.value?.triggerElement || null,
    }

    // Store the data in WeakMap instead of on the element
    focusTrapDataMap.set(el, data)

    // This is important for screen readers like NVDA that use seperate focus
    // that is changed with up and down arrow keys.
    el.setAttribute('role', 'dialog')

    // Set up the focus trap
    data.focusableElements = getFocusableElementsIn(el)

    document.addEventListener('keydown', data.boundHandleKeydown)

    // Enhance dialog usability by focusing the first interactive element.
    // Instead of the default close button focus, we prioritize the first input or non-close button,
    // improving the user's workflow when interacting with the dialog.
    if (data.focusableElements.length > 0) {
      const elementToFocus =
        el.querySelector('input') ?? el.querySelector('button:not(.close)') ?? data.focusableElements[0]

      if (elementToFocus) {
        // Use nextTick to ensure DOM is ready.
        Vue.nextTick(() => {
          elementToFocus.focus()
        })
      }
    }
  },

  update(el: HTMLElement) {
    const data = focusTrapDataMap.get(el)
    if (data) {
      // Update focusable elements (useful when buttons become enabled/disabled)
      data.focusableElements = getFocusableElementsIn(el)
    }
  },

  unbind(el: HTMLElement) {
    const data = focusTrapDataMap.get(el)
    if (data) {
      document.removeEventListener('keydown', data.boundHandleKeydown)
      // Restore focus to the trigger element if provided, otherwise use previous element
      const elementToFocus = data.triggerElement || data.previousActiveElement
      if (elementToFocus) {
        elementToFocus.focus()
      }
      // Clean up the WeakMap entry
      focusTrapDataMap.delete(el)
    }
  },
}

export default focusTrap
